/**
 * Trip CO₂ Calculator — public API client.
 *
 * Talks to the **public, no-auth** batch endpoint at
 * `POST /api/public/co2/calculate-batch`. Uses `fetch` (not the
 * authenticated axios client) so the public flow stays decoupled
 * from the JWT / refresh pipeline.
 *
 * The response shape mirrors the engine's existing `EmissionsResult`
 * with two added fields: `id` (echoed from the request) and `kind`
 * ('mode' | 'factor'). The Trip CO₂ Calculator only ever sends
 * mode-driven segments today; the factor variant is reserved for
 * future journey-aggregate use cases.
 *
 * Phase 4 of the Trip CO₂ Calculator (see PRD §11). Phase 7 will
 * attach quota / rate-limit middleware on the server; this client
 * does not need to change for that.
 */

import type { EmissionsResult, TransportMode } from '../admin/carbonAccounting/api';
import type { ConfigByMode } from './modes';
import type { LatLng } from '../../entities/geo/types';
import type { Segment } from './buildComparison';
import { testBypassQuerySuffix } from './testBypass';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

// English country-name resolver. Pinned to the 'en' locale (not the
// browser's locale) so the persisted CalculatorUsage rows stay
// consistent across users — analytics queries shouldn't have to deal
// with "Portugal" vs "Portugália" depending on who calculated. Falls
// back to whatever the caller passed (typically an ISO-2 code) when
// `Intl.DisplayNames` can't resolve.
let countryDisplay: Intl.DisplayNames | null = null;
function resolveCountryName(code: string | null | undefined): string | null | undefined {
  if (code === null || code === undefined) return code;
  if (!code) return code;
  if (code.length !== 2) return code; // already a name, or unexpected — keep as-is
  try {
    if (!countryDisplay) {
      countryDisplay = new Intl.DisplayNames(['en'], { type: 'region' });
    }
    return countryDisplay.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wire types
// ─────────────────────────────────────────────────────────────────────────────

/** A request segment that always sources distance from {from, to}. */
export interface ModeRequestSegment {
  /** Optional client identifier echoed in the response. */
  id?: string;
  mode: TransportMode;
  options?: Record<string, unknown>;
  from: LatLng;
  to: LatLng;
}

/** Mode-kind segment result — engine output + request echo. */
export interface ModeSegmentResult extends EmissionsResult {
  id?: string;
  kind: 'mode';
}

export interface BatchRequest {
  segments: ModeRequestSegment[];
  /**
   * Optional analytics annotation: which mode the user picked. The
   * backend uses this to populate the `travel_impact_calculator_calculate`
   * Mixpanel event's `mode` + per-segment-of-interest properties.
   * The engine itself ignores it.
   */
  userMode?: TransportMode;
  /**
   * Optional analytics annotation: whether this is a round-trip in
   * the user's view. The backend doubles nothing (round-trip math is
   * client-side), but the analytics event records the flag for
   * funnel slicing.
   */
  roundTrip?: boolean;
  /**
   * Origin / destination city name and country, used server-side to
   * write the `CalculatorUsage` row. Country is the **full English
   * name** (e.g. "Portugal"), resolved on the FE via
   * `Intl.DisplayNames` from the geocoder's ISO-2 code, falling back
   * to the code itself when the locale data can't resolve a name.
   * Nullable when the geocoder didn't return the corresponding value.
   *
   * Re-identification note: city + day + transport mode can in theory
   * identify a user for rare city pairs. Acceptable trade-off for
   * product analytics under legitimate interest; disclosed in the
   * privacy policy Section 19.2.
   */
  originCity?: string | null;
  originCountry?: string | null;
  destinationCity?: string | null;
  destinationCountry?: string | null;
}

export interface BatchResponse {
  results: ModeSegmentResult[];
}

/**
 * Error thrown for non-2xx responses. Carries the server-supplied
 * error envelope (`code`, `segmentIndex`, `retryAfterSeconds`) when
 * available so callers can surface specific UX (e.g. the quota-gate
 * card in Phase 7).
 */
export class TripCalculatorApiError extends Error {
  status: number;
  code?: string;
  segmentIndex?: number;
  /**
   * Seconds until the failing operation may succeed again. Populated
   * for 429 responses (rate_limited, quota_exceeded). Computed by the
   * backend off the relevant window/floor; the frontend can convert
   * to an absolute time with `Date.now() + retryAfterSeconds * 1000`.
   */
  retryAfterSeconds?: number;

  constructor(
    message: string,
    init: {
      status: number;
      code?: string;
      segmentIndex?: number;
      retryAfterSeconds?: number;
    }
  ) {
    super(message);
    this.name = 'TripCalculatorApiError';
    this.status = init.status;
    this.code = init.code;
    this.segmentIndex = init.segmentIndex;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POSTs the given segments to the public batch endpoint and returns
 * the parallel results array.
 *
 * Translates Phase 3's `Segment` (from `buildComparison.ts`) into the
 * wire shape — the only differences are the explicit `from`/`to`
 * coordinates (which only the page knows) and an optional client id.
 */
export async function calculateBatch(input: {
  origin: LatLng;
  destination: LatLng;
  segments: Segment[];
  /** User's chosen TransportMode — annotates the request for analytics only. */
  userMode?: TransportMode;
  /** Round-trip flag — annotates the request for analytics only. */
  roundTrip?: boolean;
  /** City names and ISO-2 country codes from the geocoder. The wire
   *  carries the FE-resolved full English country name; the conversion
   *  happens here so the BE doesn't have to ship a country-name table. */
  originCity?: string | null;
  originCountry?: string | null;
  destinationCity?: string | null;
  destinationCountry?: string | null;
  signal?: AbortSignal;
}): Promise<BatchResponse> {
  const body: BatchRequest = {
    segments: input.segments.map((seg) => ({
      id: seg.mode, // mode is already unique per segment in v1; echoes back for ordering safety
      mode: seg.mode,
      options: { ...(seg.config as ConfigByMode[typeof seg.mode]) } as Record<string, unknown>,
      from: input.origin,
      to: input.destination,
    })),
    ...(input.userMode !== undefined ? { userMode: input.userMode } : {}),
    ...(input.roundTrip !== undefined ? { roundTrip: input.roundTrip } : {}),
    ...(input.originCity !== undefined ? { originCity: input.originCity } : {}),
    ...(input.originCountry !== undefined
      ? { originCountry: resolveCountryName(input.originCountry) }
      : {}),
    ...(input.destinationCity !== undefined ? { destinationCity: input.destinationCity } : {}),
    ...(input.destinationCountry !== undefined
      ? { destinationCountry: resolveCountryName(input.destinationCountry) }
      : {}),
  };

  // Forward `?test=1` from the page URL to the backend so the
  // QA bypass on the quota / rate-limit middleware actually applies.
  // See `./testBypass.ts` for the rule.
  const response = await fetch(
    `${API_BASE}/public/co2/calculate-batch${testBypassQuerySuffix()}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: input.signal,
    }
  );

  if (!response.ok) {
    let payload: {
      error?: string;
      code?: string;
      segmentIndex?: number;
      retryAfterSeconds?: number;
    } = {};
    try {
      payload = await response.json();
    } catch {
      // Body wasn't JSON, fall through with empty payload.
    }
    // For 429s today the backend puts the machine code in `error`
    // (e.g. 'quota_exceeded', 'rate_limited') and does not send a
    // separate `code` field. Falling back to `payload.error` keeps
    // callers' `err.code === 'quota_exceeded'` checks working without
    // a coordinated backend rename, and stays forward-compatible if
    // the backend later starts sending an explicit `code` field.
    throw new TripCalculatorApiError(payload.error || `HTTP ${response.status}`, {
      status: response.status,
      code: payload.code ?? payload.error,
      segmentIndex: payload.segmentIndex,
      retryAfterSeconds: payload.retryAfterSeconds,
    });
  }

  return (await response.json()) as BatchResponse;
}
